import React, { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { EventScheduleQuery } from 'queries';
import useModal from 'hooks/useModal';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { IScheduleItem, ScheduleItemInputType } from 'types';
import {
  DraftType,
  emptyDraft,
  findOverlaps,
  formatTimeRange,
  mergeItem,
  TIME_REGEX,
  toDraft,
  toInput,
} from './utils';

const CONFLICT_MESSAGE =
  '다른 관리자가 먼저 일정을 저장했습니다. 새로고침 후 다시 시도해 주세요';
const LIVE_MESSAGE = '저장 시 일정이 즉시 반영되고,\n유저에게 노출됩니다.';
const ASK_MESSAGE = '그대로 저장할까요?';

const confirmBody = (children: React.ReactNode) => (
  <div
    css={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      maxWidth: 360,
      lineHeight: 1.6,
      textAlign: 'center',
      whiteSpace: 'pre-line',
      wordBreak: 'keep-all',
    }}
  >
    {children}
  </div>
);

const askText = <p css={{ marginTop: 4, fontWeight: 600 }}>{ASK_MESSAGE}</p>;

type ErrorBody = { message?: string | string[] };

const getStatus = (error: unknown) =>
  isAxiosError(error) ? error.response?.status : undefined;

const getErrorMessage = (error: unknown) => {
  const raw = isAxiosError<ErrorBody>(error)
    ? error.response?.data?.message
    : undefined;
  if (Array.isArray(raw)) return raw.join('\n');
  return typeof raw === 'string' ? raw : DEFAULT_ERROR_MESSAGE;
};

const DRAFT_ID = '__draft__';

const toInputs = (items: IScheduleItem[]): ScheduleItemInputType[] =>
  items.map((item) => ({ ...item }));

function useSchedule(eventId: number) {
  const { handleAlert, handleConfirm } = useModal();
  const { data, refetch } = EventScheduleQuery.useGet(eventId);
  const { mutate: put, isPending: isSaving } = EventScheduleQuery.usePut();
  const { mutate: patchPublished, isPending: isPublishing } =
    EventScheduleQuery.useUpdatePublished();

  const [selectedDate, setSelectedDate] = useState('');
  const [draft, setDraft] = useState<DraftType | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const isLive = !!data?.published;

  const dates = useMemo(() => data?.dates ?? [], [data]);

  useEffect(() => {
    if (dates.length && !dates.includes(selectedDate)) {
      setSelectedDate(dates[0]);
    }
  }, [dates, selectedDate]);

  const dayItems = useMemo(
    () => items.filter((v) => v.date === selectedDate),
    [items, selectedDate],
  );

  const previewDate = draft?.date || selectedDate;
  const previewDraftId = draft ? draft.id ?? DRAFT_ID : undefined;
  const previewItems = useMemo(() => {
    const base = toInputs(items);
    if (!draft || !TIME_REGEX.test(draft.startTime)) {
      return base.filter((v) => v.date === previewDate);
    }
    const input = toInput(draft);
    return mergeItem(base, {
      ...input,
      id: previewDraftId,
      endTime: TIME_REGEX.test(input.endTime ?? '') ? input.endTime : null,
      title: input.title || '(일정명)',
    }).filter((v) => v.date === previewDate);
  }, [items, draft, previewDate, previewDraftId]);

  const save = (
    nextItems: ScheduleItemInputType[],
    successMessage: string,
    onSuccess?: () => void,
  ) => {
    put(
      {
        eventId,
        items: nextItems,
        ...(data?.version != null && { version: data.version }),
      },
      {
        onSuccess: () => {
          toast.success(successMessage);
          onSuccess?.();
        },
        onError: (error) => {
          const status = getStatus(error);
          if (status === 409) {
            handleAlert(CONFLICT_MESSAGE);
            refetch();
            return;
          }
          if (status === 403) return;
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  const handleOpenCreate = () => setDraft(emptyDraft(selectedDate));
  const handleOpenEdit = (item: IScheduleItem) => setDraft(toDraft(item));
  const handleOpenDuplicate = (item: IScheduleItem) =>
    setDraft(toDraft(item, false));
  const handleCloseDraft = () => setDraft(null);

  const handleSubmitDraft = (value: DraftType) => {
    const target = toInput(value);
    const others = toInputs(items);
    const overlaps = findOverlaps(target, others);
    const messages: React.ReactNode[] = [];

    if (overlaps.length) {
      messages.push(
        <div key="overlap" css={{ width: '100%' }}>
          <p>중복된 일정이 있습니다.</p>
          <ul
            css={{
              marginTop: 8,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#f7f7f7',
              fontSize: 14,
              color: '#666',
              textAlign: 'left',
              '> li + li': { marginTop: 4 },
            }}
          >
            {overlaps.map((v) => (
              <li key={v.id}>
                <strong css={{ marginRight: 6 }}>{formatTimeRange(v)}</strong>
                {v.title}
              </li>
            ))}
          </ul>
        </div>,
      );
    }
    if (target.id && isLive) {
      messages.push(<p key="live">{LIVE_MESSAGE}</p>);
    }

    const run = () =>
      save(
        mergeItem(others, target),
        target.id ? '일정이 수정되었습니다.' : '일정이 추가되었습니다.',
        () => {
          setSelectedDate(target.date);
          setDraft(null);
        },
      );

    if (messages.length === 0) {
      run();
      return;
    }

    handleConfirm({
      confirmContent: confirmBody(
        <>
          {messages}
          {askText}
        </>,
      ),
      yesTitle: '네, 저장할게요',
      noTitle: '아니오',
      confirmNext: run,
    });
  };

  const handleDelete = (item: IScheduleItem) => {
    handleConfirm({
      confirmContent: confirmBody(
        <>
          <p>
            <strong>
              [{formatTimeRange(item)}] {item.title}
            </strong>
            {'\n일정을 삭제합니다.'}
          </p>
          {isLive ? (
            <>
              <p>{LIVE_MESSAGE}</p>
              {askText}
            </>
          ) : (
            <p css={{ marginTop: 4, fontWeight: 600 }}>삭제할까요?</p>
          )}
        </>,
      ),
      yesTitle: isLive ? '네, 저장할게요' : '네, 삭제할게요',
      noTitle: '아니오',
      confirmNext: () =>
        save(
          toInputs(items).filter((v) => v.id !== item.id),
          '일정이 삭제되었습니다.',
          () => {
            if (draft?.id === item.id) setDraft(null);
          },
        ),
    });
  };

  const canMove = (index: number, direction: -1 | 1) => {
    const current = dayItems[index];
    const target = dayItems[index + direction];
    return !!target && target.startTime === current.startTime;
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    if (!canMove(index, direction)) return;
    const a = dayItems[index].id;
    const b = dayItems[index + direction].id;
    const next = toInputs(items);
    const ai = next.findIndex((v) => v.id === a);
    const bi = next.findIndex((v) => v.id === b);
    [next[ai], next[bi]] = [next[bi], next[ai]];
    const run = () => save(next, '순서가 변경되었습니다.');
    if (!isLive) {
      run();
      return;
    }
    handleConfirm({
      confirmContent: confirmBody(
        <>
          <p>{LIVE_MESSAGE}</p>
          {askText}
        </>,
      ),
      yesTitle: '네, 저장할게요',
      noTitle: '아니오',
      confirmNext: run,
    });
  };

  const isGrouped = (index: number) =>
    dayItems.some(
      (v, i) => i !== index && v.startTime === dayItems[index].startTime,
    );

  const handleTogglePublished = () => {
    if (!data) return;
    const published = !data.published;
    patchPublished(
      { eventId, published },
      {
        onSuccess: () =>
          toast.success(
            published
              ? '일정이 공개되었습니다.'
              : '일정이 비공개로 전환되었습니다.',
          ),
        onError: (error) => {
          if (getStatus(error) === 403) return;
          toast.error(getErrorMessage(error));
        },
      },
    );
  };

  return {
    data,
    dates,
    selectedDate,
    setSelectedDate,
    dayItems,
    draft,
    setDraft,
    previewDate,
    previewItems,
    previewDraftId,
    isSaving,
    isPublishing,
    canMove,
    isGrouped,
    handleMove,
    handleOpenCreate,
    handleOpenEdit,
    handleOpenDuplicate,
    handleCloseDraft,
    handleSubmitDraft,
    handleDelete,
    handleTogglePublished,
  };
}

export default useSchedule;
